/**
 * Geolocation — Capacitor on native, `navigator.geolocation` on web.
 *
 * We default to a coarse accuracy because the only consumer (right now) is
 * "which city is this person near" — not turn-by-turn anything. On iOS that
 * also means we ask for the lower-friction `whenInUse` permission and skip
 * NSLocationAlwaysAndWhenInUseUsageDescription. The Info.plist purpose strings
 * are documented in `apps/mobile-native/README.md`.
 */

import { isNative } from '../runtime/index.ts';

export interface Coords {
  latitude: number;
  longitude: number;
  /** Metres. Coarse-mode reads are typically ~1–3km. */
  accuracy: number;
}

export interface LocationOk {
  ok: true;
  coords: Coords;
}
export interface LocationError {
  ok: false;
  reason: 'permission-denied' | 'unavailable' | 'timeout' | string;
}

export async function getCurrentLocation(opts: {
  highAccuracy?: boolean;
  timeoutMs?: number;
} = {}): Promise<LocationOk | LocationError> {
  const enableHighAccuracy = opts.highAccuracy ?? false;
  const timeout = opts.timeoutMs ?? 10_000;

  if (isNative()) {
    try {
      const { Geolocation } = await import('@capacitor/geolocation');
      // 'coarseLocation' alias asks only for ACCESS_COARSE_LOCATION on Android
      // (the 'location' alias would also ask for ACCESS_FINE_LOCATION, which we
      // don't need + don't want to declare in the manifest). On iOS the alias
      // is ignored — there's only one location permission. v1 never needs
      // street-level accuracy: we use the lat/lng to suggest gatherings near
      // the user's city, where coarse (~few km) precision is plenty.
      const perm = await Geolocation.requestPermissions({ permissions: ['coarseLocation'] });
      // Android reports the result under perm.coarseLocation when we request
      // the coarseLocation alias; iOS reports under perm.location regardless
      // (only one permission). Read both — the first defined one wins.
      const permState = (perm as { coarseLocation?: string; location?: string }).coarseLocation
        ?? (perm as { location?: string }).location;
      if (permState !== 'granted' && permState !== 'prompt-with-rationale') {
        return { ok: false, reason: 'permission-denied' };
      }
      const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy, timeout });
      return {
        ok: true,
        coords: {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        },
      };
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (/denied/i.test(msg)) return { ok: false, reason: 'permission-denied' };
      if (/timeout/i.test(msg)) return { ok: false, reason: 'timeout' };
      return { ok: false, reason: msg };
    }
  }

  // Web fallback.
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return { ok: false, reason: 'unavailable' };
  }
  return new Promise<LocationOk | LocationError>((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({
        ok: true,
        coords: { latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy: p.coords.accuracy },
      }),
      (err) => {
        if (err.code === err.PERMISSION_DENIED) resolve({ ok: false, reason: 'permission-denied' });
        else if (err.code === err.TIMEOUT) resolve({ ok: false, reason: 'timeout' });
        else resolve({ ok: false, reason: 'unavailable' });
      },
      { enableHighAccuracy, timeout, maximumAge: 60_000 },
    );
  });
}
